import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Brain, TrendingUp, Scale, Compass, Send, ExternalLink, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const CONF = { high: "bg-teal-100 text-teal-700", medium: "bg-sky-100 text-sky-700", low: "bg-amber-100 text-amber-700", insufficient: "bg-slate-100 text-slate-500" };
const REVIEW = { verified: "bg-teal-100 text-teal-700", pending: "bg-amber-100 text-amber-700", needs_update: "bg-red-100 text-red-700" };

export default function Intelligence() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [preds, setPreds] = useState(null);
  const [state, setState] = useState("ohio");
  const [reg, setReg] = useState(null);
  const [expansion, setExpansion] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager"].includes(user.role)) { navigate("/dashboard"); return; }
    api.get("/intel/predictions").then((r) => setPreds(r.data)).catch(() => {});
    api.get(`/intel/regulatory?state=${state}`).then((r) => setReg(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  const loadReg = (s) => { setState(s); api.get(`/intel/regulatory?state=${s}`).then((r) => setReg(r.data)).catch(() => {}); };
  const runExpansion = async () => {
    setBusy(true);
    try { const { data } = await api.post("/intel/expansion", {}); setExpansion(data); }
    catch { toast.error("Expansion AI unavailable"); } finally { setBusy(false); }
  };

  if (loading || !user || !preds) return <div className="py-32 grid place-items-center bg-blue-950 min-h-[70vh]"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;

  return (
    <div className="bg-blue-950 text-white min-h-screen" data-testid="intelligence-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-2 mb-1"><Brain className="w-5 h-5 text-teal-400" /><p className="text-xs uppercase tracking-[0.18em] text-teal-400 font-semibold">T5 Intelligence</p></div>
        <h1 className="font-display text-3xl lg:text-5xl font-bold">Data & multi-state intelligence</h1>
        <p className="text-blue-200/70 mt-2">Predictive operations, sourced regulatory records and expansion guidance — grounded in real data, honest about uncertainty.</p>

        <Tabs defaultValue="predictions" className="mt-8">
          <TabsList className="bg-white/5 flex-wrap h-auto" data-testid="intel-tabs">
            <TabsTrigger value="predictions" data-testid="intel-tab-predictions" className="data-[state=active]:bg-teal-500">Predictive Ops</TabsTrigger>
            <TabsTrigger value="regulatory" data-testid="intel-tab-regulatory" className="data-[state=active]:bg-teal-500">Regulatory</TabsTrigger>
            <TabsTrigger value="expansion" data-testid="intel-tab-expansion" className="data-[state=active]:bg-teal-500">Expansion AI</TabsTrigger>
          </TabsList>

          <TabsContent value="predictions" className="mt-6">
            <div className="grid sm:grid-cols-2 gap-4" data-testid="predictions-grid">
              {preds.predictions.map((p, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`prediction-${i}`}>
                  <div className="flex items-center justify-between"><TrendingUp className="w-5 h-5 text-teal-300" /><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${CONF[p.confidence]}`}>{p.confidence}{p.estimate ? " · est" : ""}</span></div>
                  <p className="font-display text-2xl font-bold mt-2">{p.value}</p>
                  <p className="text-sm text-blue-100 mt-0.5">{p.metric}</p>
                  <p className="text-xs text-blue-200/60 mt-2">{p.basis}</p>
                </motion.div>
              ))}
            </div>
            <p className="text-xs text-amber-300/80 mt-4 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {preds.disclaimer}</p>
          </TabsContent>

          <TabsContent value="regulatory" className="mt-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-blue-200/70">State:</span>
              <select value={state} onChange={(e) => loadReg(e.target.value)} data-testid="reg-state-select" className="h-9 rounded-lg bg-white/10 border border-white/15 text-white px-3 text-sm">
                {["ohio", "california"].map((s) => <option key={s} value={s} className="text-slate-900">{s}</option>)}
              </select>
            </div>
            <div className="space-y-3" data-testid="reg-records">
              {reg?.records?.length ? reg.records.map((r) => (
                <div key={r.id} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`reg-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-display font-bold text-lg">{r.topic}</h3>
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${REVIEW[r.human_review]}`}>{r.human_review === "verified" ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> verified</span> : r.human_review}</span>
                  </div>
                  <p className="text-sm text-blue-100 mt-2">{r.summary}</p>
                  <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-blue-200/70">
                    <span>Jurisdiction: {r.jurisdiction} · {r.agency}</span>
                    <span>Effective: {r.effective_date || "—"} · Retrieved: {r.retrieved_date}</span>
                    <span>Affects: {r.product_affected || "—"}</span>
                    <a href={r.url} target="_blank" rel="noreferrer" className="text-teal-300 hover:underline inline-flex items-center gap-1">{r.source} <ExternalLink className="w-3 h-3" /></a>
                  </div>
                </div>
              )) : <p className="text-blue-200/60">No verified records for this state yet.</p>}
            </div>
            {reg?.disclaimer && <p className="text-xs text-amber-300/80 mt-4 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {reg.disclaimer}</p>}
          </TabsContent>

          <TabsContent value="expansion" className="mt-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="expansion-panel">
              <div className="flex items-center gap-2"><Compass className="w-5 h-5 text-teal-300" /><h3 className="font-display font-bold text-lg">Where should T5 grow next?</h3></div>
              <p className="text-sm text-blue-200/70 mt-1">Ask the Expansion AI to weigh demand, caregiver supply, B2B interest and regulatory complexity from current data.</p>
              <Button onClick={runExpansion} disabled={busy} data-testid="expansion-run" className="mt-4 bg-teal-500 hover:bg-teal-600 gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Analyze expansion options</Button>
              {expansion && (
                <div className="mt-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {Object.entries(expansion.signals).map(([k, v]) => (
                      <div key={k} className="bg-white/5 rounded-xl p-3"><p className="font-display text-xl font-bold">{String(v)}</p><p className="text-[11px] text-blue-200/60 capitalize">{k.replace(/_/g, " ")}</p></div>
                    ))}
                  </div>
                  <div className="mt-4 bg-blue-900/40 border border-teal-400/20 rounded-xl p-4 text-blue-50 whitespace-pre-wrap leading-relaxed" data-testid="expansion-answer">{expansion.reply}</div>
                  <p className="text-xs text-amber-300/80 mt-3 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {expansion.disclaimer}</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
